/**
 * One conversation as the screen holds it (agent-1-web W1): a pure reducer, so
 * every state the six-state table names is reachable from a test without a
 * component, a timer or a network.
 *
 * Messages are kept in the SERVER's `ChatMessage` shape whether they came from
 * the live stream or from `GET /chat/sessions/{id}`. That is what makes reload
 * parity (WC7) a property of the code rather than a promise: the live events
 * are folded into exactly the record the reload returns -- text, cards, claims,
 * caveats, source, reason, check, retryable and the done tool steps -- and one
 * set of components renders both.
 */
import type {
  ChatContext,
  ChatMessage,
  ChatSession,
  ChatSessionDetail,
  ChatStreamEvent,
  ChatToolStep,
  FallbackReason,
} from './assistant'
import { stripMessage } from './chatText'

/** Why the composer cannot be used right now, or what went wrong with a turn. */
export type ThreadNotice =
  /** Every chat route answered the plain not-found: the backend flag is off (C71). */
  | { kind: 'absent' }
  /**
   * 409 / 429 / 503 before the stream. `retryAt` is epoch ms, from `Retry-After`.
   * `chat.paused` (agent-1.1 S6) is an administrator's runtime switch: the same
   * refusal, its wait five minutes, its words fixed (no countdown).
   */
  | { kind: 'refused'; code: RefusalCode; retryAt: number | null }
  /** 400: the server refused the text itself. */
  | { kind: 'invalid' }
  /** The stream stopped early; resending the SAME id replays or 409s (C48). */
  | {
      kind: 'dropped'
      clientMessageId: string
      text: string
      context: ChatContext
      /** The server's id for the user message, when the `session` event had named it. */
      userMessageId?: string | null
      /** A resend answered 409: the turn exists and is still being written. */
      stillWriting?: boolean
      code?: RefusalCode
      retryAt?: number | null
    }
  /** `error` (internal.error): never retryable; reload the conversation (C66). */
  | { kind: 'error' }
  /** The conversation no longer exists (deleted elsewhere): start a new one. */
  | { kind: 'gone' }
  /** Anything else: a network failure on a plain call, a 5xx, a protocol error. */
  | { kind: 'failed' }

export type RefusalCode = 'chat.turn_in_progress' | 'chat.rate_limited' | 'chat.busy' | 'chat.paused'

export const REFUSAL_CODES: readonly RefusalCode[] = ['chat.turn_in_progress', 'chat.rate_limited', 'chat.busy', 'chat.paused']

export interface PendingTurn {
  clientMessageId: string
  text: string
  context: ChatContext
  /** Set by the `session` event: the server's id for the user message. */
  userMessageId?: string
  /** Set by `send` when this turn re-sends a dropped one with its own id (C48). */
  resend?: boolean
}

export interface ThreadState {
  session: ChatSession | null
  /** Selected conversation while its read is unresolved. */
  loading: string | null
  /** Oldest first, as the server pages them. */
  messages: ChatMessage[]
  /** The cursor for "show earlier": null at the start of the conversation. */
  nextBefore: string | null
  /** The turn being sent or streamed; the composer is disabled while it is set. */
  turn: PendingTurn | null
  notice: ThreadNotice | null
  /**
   * The assistant message that most recently COMPLETED through the live stream,
   * for the screen reader's live region. Set by `reply`/`fallback` events only,
   * so a reload or "show earlier" announces nothing -- and kept in the state
   * rather than inferred from renders, because a turn whose frames arrive in
   * one chunk is folded in one batch and is never rendered pending.
   */
  announce: string | null
}

export const EMPTY_THREAD: ThreadState = {
  session: null,
  loading: null,
  messages: [],
  nextBefore: null,
  turn: null,
  notice: null,
  announce: null,
}

export type ThreadAction =
  | { type: 'reset' }
  | { type: 'select'; id: string }
  | { type: 'selectionFailed'; notice: ThreadNotice }
  /** The conversation the turn belongs to; null forgets it, so the next send starts a new one. */
  | { type: 'session'; session: ChatSession | null }
  /** The conversation re-read after a turn: the server may have renamed it (session-titles). */
  | { type: 'renamed'; session: ChatSession }
  | { type: 'loaded'; detail: ChatSessionDetail }
  | { type: 'earlier'; detail: ChatSessionDetail }
  | { type: 'send'; turn: PendingTurn; now: string }
  | { type: 'event'; frame: ChatStreamEvent }
  | { type: 'settled' }
  | { type: 'notice'; notice: ThreadNotice }
  | { type: 'dismiss' }

/** A user message shown before the server has named it (the `session` event does). */
function optimistic(turn: Pick<PendingTurn, 'clientMessageId' | 'text'>, now: string): ChatMessage {
  return {
    id: turn.clientMessageId,
    role: 'user',
    status: 'complete',
    text: turn.text,
    source: 'user',
    reason: null,
    check: null,
    retryable: false,
    cards: [],
    claims: [],
    caveats: [],
    tool_steps: [],
    model_id: null,
    prompt_version: null,
    created_at: now,
  }
}

function pendingAssistant(id: string, createdAt: string): ChatMessage {
  return {
    id,
    role: 'assistant',
    status: 'pending',
    text: null,
    source: null,
    reason: null,
    check: null,
    retryable: false,
    cards: [],
    claims: [],
    caveats: [],
    tool_steps: [],
    model_id: null,
    prompt_version: null,
    created_at: createdAt,
  }
}

/**
 * `items` with the LAST item that matches replaced by `f(item)`, or `items`
 * itself when none does. Written over a reversed copy so it needs no index
 * arithmetic (`clientScience.test.ts` inventories every operator in the client).
 */
function replaceLast<T>(items: readonly T[], match: (item: T) => boolean, f: (item: T) => T): T[] | null {
  const reversed = [...items].reverse()
  const at = reversed.findIndex(match)
  if (at === -1) return null
  return reversed.map((item, i) => (i === at ? f(item) : item)).reverse()
}

/**
 * A running step is replaced by its own `done` or `failed` step, so a finished
 * turn holds the same steps the reload carries (done and failed, never running).
 */
function foldStep(steps: ChatToolStep[], step: ChatToolStep): ChatToolStep[] {
  if (step.status === 'running') return [...steps, step]
  return replaceLast(steps, (s) => s.tool === step.tool && s.status === 'running', () => step) ?? [...steps, step]
}

function mapLast(messages: ChatMessage[], id: string, f: (m: ChatMessage) => ChatMessage): ChatMessage[] {
  return replaceLast(messages, (m) => m.id === id, f) ?? messages
}

/** The newest message, if it is the assistant's. */
function lastAssistant(messages: readonly ChatMessage[]): ChatMessage | undefined {
  const last = messages.at(-1)
  return last?.role === 'assistant' ? last : undefined
}

function applyEvent(state: ThreadState, frame: ChatStreamEvent): ThreadState {
  const turn = state.turn
  switch (frame.event) {
    case 'session': {
      const { user_message_id: userId, assistant_message_id: assistantId } = frame.data
      // A replay names a turn the screen may already hold (reloaded, or shown
      // before the drop): drop any earlier copy of either message first.
      const kept = state.messages.filter(
        (m) => m.id !== userId && m.id !== assistantId && m.id !== turn?.clientMessageId,
      )
      const at = new Date().toISOString()
      // The server stores the STRIPPED text (C70), so the live bubble shows that too.
      const user = { ...optimistic({ clientMessageId: userId, text: stripMessage(turn?.text ?? '') }, at), id: userId }
      return {
        ...state,
        turn: turn ? { ...turn, userMessageId: userId } : turn,
        // The state as it stood BEFORE this turn: the server writes the new one
        // when the turn completes and no event carries it, so a reload is the
        // only way to see it (replay.sse's `session` carries it after the turn).
        session: state.session ? { ...state.session, state: frame.data.state } : state.session,
        messages: [...kept, user, pendingAssistant(assistantId, at)],
      }
    }
    case 'tool_step': {
      const last = lastAssistant(state.messages)
      if (!last) return state
      return { ...state, messages: mapLast(state.messages, last.id, (m) => ({ ...m, tool_steps: foldStep(m.tool_steps, frame.data) })) }
    }
    case 'reply': {
      const d = frame.data
      return {
        ...state,
        announce: d.message_id,
        messages: mapLast(state.messages, d.message_id, (m) => ({
          ...m,
          status: 'complete',
          text: d.text,
          source: d.source,
          reason: null,
          check: null,
          retryable: false,
          cards: d.cards,
          claims: d.claims,
          caveats: d.caveats,
        })),
      }
    }
    case 'fallback': {
      const d = frame.data
      return {
        ...state,
        announce: d.message_id,
        messages: mapLast(state.messages, d.message_id, (m) => ({
          ...m,
          status: 'complete',
          text: d.text,
          source: d.source,
          reason: d.reason,
          check: d.check,
          retryable: d.retryable,
          cards: d.cards,
          claims: [],
          caveats: d.caveats,
        })),
      }
    }
    case 'done': {
      const last = lastAssistant(state.messages)
      if (!last) return { ...state, turn: null }
      return {
        ...state,
        turn: null,
        messages: mapLast(state.messages, last.id, (m) => ({
          ...m,
          model_id: frame.data.model_id,
          prompt_version: frame.data.prompt_version,
        })),
      }
    }
    case 'error':
      // The message stays pending on the server until it is finalised; a send
      // meanwhile is 409 (C66). The screen says so and offers a reload.
      return { ...state, turn: null, notice: { kind: 'error' } }
  }
}

export function threadReducer(state: ThreadState, action: ThreadAction): ThreadState {
  switch (action.type) {
    case 'reset':
      return EMPTY_THREAD
    case 'select':
      return { ...EMPTY_THREAD, loading: action.id }
    case 'selectionFailed':
      return { ...EMPTY_THREAD, notice: action.notice }
    case 'session':
      return { ...state, session: action.session }
    case 'renamed':
      // Only the title: the rest of the session is the thread's own, and a
      // re-read for a conversation no longer open changes nothing.
      if (!state.session || action.session.id !== state.session.id || action.session.title === state.session.title) return state
      return { ...state, session: { ...state.session, title: action.session.title } }
    case 'loaded':
      return {
        ...state,
        loading: null,
        session: action.detail.session,
        messages: action.detail.messages,
        nextBefore: action.detail.next_before,
        turn: null,
        notice: null,
        announce: null,
      }
    case 'earlier': {
      // A page read for another conversation (one opened, or a new one started,
      // while it was in flight) is not this one's history (final review).
      if (action.detail.session.id !== state.session?.id) return state
      const known = new Set(state.messages.map((m) => m.id))
      const older = action.detail.messages.filter((m) => !known.has(m.id))
      return { ...state, messages: [...older, ...state.messages], nextBefore: action.detail.next_before }
    }
    case 'send': {
      // A resend of a dropped turn reuses its id, and its bubble is already
      // there -- under the client id, or under the server's if `session` had
      // renamed it before the drop (W1 review).
      const dropped = state.notice?.kind === 'dropped' && state.notice.clientMessageId === action.turn.clientMessageId
        ? state.notice
        : null
      const ids = new Set([action.turn.clientMessageId, dropped?.userMessageId].filter(Boolean))
      const already = state.messages.some((m) => ids.has(m.id))
      const turn: PendingTurn = dropped
        ? { ...action.turn, resend: true, userMessageId: dropped.userMessageId ?? undefined }
        : action.turn
      return {
        ...state,
        turn,
        notice: null,
        messages: already ? state.messages : [...state.messages, optimistic(action.turn, action.now)],
      }
    }
    case 'event':
      return applyEvent(state, action.frame)
    case 'settled':
      return { ...state, turn: null }
    case 'notice': {
      // A refusal before the stream means the message was never accepted: its
      // optimistic bubble goes (the provider puts the text back in the
      // composer). Once the stream has started the `session` event has renamed
      // that bubble to the server's id, so nothing matches and the turn stays --
      // the server holds it. A drop keeps it either way: the turn may be stored.
      const turn = state.turn
      // A RESEND that fails is still a stored turn (the server may hold it, and
      // a 409 says it does): its bubble stays, and the notice stays "dropped"
      // so the same id can be tried again -- never a new id, which would be a
      // second turn (W1 review).
      if (turn?.resend && ['refused', 'invalid', 'failed', 'dropped'].includes(action.notice.kind)) {
        return {
          ...state,
          turn: null,
          notice: {
            kind: 'dropped',
            clientMessageId: turn.clientMessageId,
            text: turn.text,
            context: turn.context,
            userMessageId: turn.userMessageId ?? null,
            stillWriting: action.notice.kind === 'refused' && action.notice.code === 'chat.turn_in_progress',
            code: action.notice.kind === 'refused' ? action.notice.code : undefined,
            retryAt: action.notice.kind === 'refused' ? action.notice.retryAt : null,
          },
        }
      }
      const notice: ThreadNotice =
        action.notice.kind === 'dropped' ? { ...action.notice, userMessageId: turn?.userMessageId ?? null } : action.notice
      const messages =
        turn && action.notice.kind !== 'dropped' ? state.messages.filter((m) => m.id !== turn.clientMessageId) : state.messages
      return { ...state, turn: null, notice, messages }
    }
    case 'dismiss':
      return { ...state, notice: null }
  }
}

// ── what a message is, for the screen ─────────────────────────────────────

/** The reasons that are an honest decline with a next step, not a failure (C52). */
const DECLINE_REASONS: ReadonlySet<FallbackReason> = new Set(['out_of_scope', 'no_reading', 'reading_unsupported'])

export type MessageKind = 'user' | 'thinking' | 'reply' | 'declined' | 'fallback'

export function messageKind(m: ChatMessage): MessageKind {
  if (m.role === 'user') return 'user'
  if (m.status === 'pending') return 'thinking'
  if (m.source === 'model') return 'reply'
  if (m.reason && DECLINE_REASONS.has(m.reason)) return 'declined'
  return 'fallback'
}

/** Shared by every send control and by the provider itself. */
export function retryDeadline(notice: ThreadNotice | null): number | null {
  return notice?.kind === 'refused' || notice?.kind === 'dropped' ? notice.retryAt ?? null : null
}
